import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Edit,
  Plus,
  Trash2,
  Search,
  Megaphone,
  FolderTree,
} from "lucide-react";
import api from "../../../api/api";

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/announcement-categories");
      setCategories(res.data || []);
    } catch (error) {
      console.error("Failed to fetch announcement categories:", error);
    }
  };

  const fetchAnnouncements = async () => {
    setLoading(true);

    try {
      const params = {
        limit: 100,
      };

      if (search) params.search = search;
      if (status) params.status = status;
      if (categoryId) params.category_id = categoryId;

      const res = await api.get("/announcements", { params });
      setAnnouncements(res.data.items || []);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this announcement?"
    );

    if (!confirmed) return;

    try {
      await api.delete(`/announcements/${id}`);
      fetchAnnouncements();
    } catch (error) {
      console.error("Failed to delete announcement:", error);
      alert("Failed to delete announcement.");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchAnnouncements();
    }, 400);

    return () => clearTimeout(delay);
  }, [search, status, categoryId]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.3em] text-[#F4B000]">
            CMS
          </p>

          <h1 className="text-2xl md:text-3xl font-black text-[#061A40] mt-2">
            Announcements
          </h1>

          <p className="text-sm text-slate-500 mt-1">
            Manage official public notices and homepage announcements.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/announcement-categories"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-white border border-slate-200 text-[#061A40] font-bold hover:bg-slate-50"
          >
            <FolderTree size={18} />
            Categories
          </Link>

          <Link
            to="/admin/announcements/create"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#061A40] text-white font-bold hover:bg-[#0A2A62]"
          >
            <Plus size={18} />
            New Announcement
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search announcements..."
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
            />
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
            <option value="archived">Archived</option>
          </select>

          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 font-bold">
            Loading announcements...
          </div>
        ) : announcements.length === 0 ? (
          <div className="p-16 text-center">
            <Megaphone size={48} className="mx-auto text-slate-300" />

            <h2 className="text-xl font-black text-[#061A40] mt-4">
              No announcements found
            </h2>

            <p className="text-slate-500 mt-2">
              Create your first official announcement.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-4 text-xs uppercase tracking-widest text-slate-500">
                    Title
                  </th>
                  <th className="px-5 py-4 text-xs uppercase tracking-widest text-slate-500">
                    Category
                  </th>
                  <th className="px-5 py-4 text-xs uppercase tracking-widest text-slate-500">
                    Status
                  </th>
                  <th className="px-5 py-4 text-xs uppercase tracking-widest text-slate-500">
                    Date
                  </th>
                  <th className="px-5 py-4 text-xs uppercase tracking-widest text-slate-500 text-right">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {announcements.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <p className="font-black text-[#061A40]">
                        {item.title}
                      </p>

                      {item.summary && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1">
                          {item.summary}
                        </p>
                      )}
                    </td>

                    <td className="px-5 py-4 text-sm text-slate-600">
                      {item.category?.name || "Uncategorized"}
                    </td>

                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-black capitalize ${
                          item.status === "published"
                            ? "bg-green-100 text-green-700"
                            : item.status === "draft"
                            ? "bg-slate-100 text-slate-700"
                            : item.status === "scheduled"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>

                    <td className="px-5 py-4 text-sm text-slate-600">
                      {item.published_at
                        ? new Date(item.published_at).toLocaleDateString()
                        : new Date(item.created_at).toLocaleDateString()}
                    </td>

                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/admin/announcements/edit/${item.id}`}
                          className="w-10 h-10 rounded-xl bg-slate-100 text-[#061A40] hover:bg-[#061A40] hover:text-white flex items-center justify-center"
                        >
                          <Edit size={17} />
                        </Link>

                        <button
                          onClick={() => deleteAnnouncement(item.id)}
                          className="w-10 h-10 rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center"
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}